import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexLegend,
  ApexNonAxisChartSeries,
  ApexPlotOptions,
  ApexResponsive,
  ApexStroke,
  ApexTooltip,
  ApexXAxis,
  NgApexchartsModule
} from 'ng-apexcharts';

import {MatCardModule} from '@angular/material/card';
import {ChatbotMetrics} from "./chatbot-metrics.model";

@Component({
  selector: 'app-chatbot-report',
  standalone: true,
    imports: [MatCardModule, NgApexchartsModule],
  templateUrl: './chatbot-report.component.html'
})
export class ChatbotReportComponent implements OnChanges {
  @Input({transform: (value: ChatbotMetrics[] | null | undefined) => value ?? []})
  metricsData: ChatbotMetrics[] = [];

  // --- Summary KPIs ---
  // Note: totalSent -> AI Responses; totalResponses -> User Messages.
  summary = {
    totalDisplayed: 0,
    totalOpened: 0,
    totalSent: 0,      // AI Responses
    totalBlocked: 0,
    totalRouted: 0,
    totalResponses: 0, // User Messages
    avgResponse: 0,    // in seconds
  };

  // --- Timeline Chart (Avg Response Time Over Time) ---
  timelineSeries: ApexAxisChartSeries = [];
  timelineChart!: ApexChart;
  timelineXAxis!: ApexXAxis;
  timelineStroke!: ApexStroke;
  timelineTooltip!: ApexTooltip;

  // --- Issues Donut Chart (Blocked vs. Routed) ---
  issuesSeries: ApexNonAxisChartSeries = [];
  issuesChart!: ApexChart;
  issuesLabels: string[] = [];
  issuesTooltip!: ApexTooltip;

  // --- Usage Stacked Bar Chart (Usage Metrics by Date) ---
  usageSeries: ApexAxisChartSeries = [];
  usageChart!: ApexChart;
  usageXAxis!: ApexXAxis;
  usagePlotOptions!: ApexPlotOptions;
  usageDataLabels!: ApexDataLabels;
  usageLegend!: ApexLegend;
  usageResponsive!: ApexResponsive[];

  ngOnChanges(changes: SimpleChanges): void {
      if (changes['metricsData'] && this.metricsData.length) {
      this.computeSummary();
      this.buildTimelineChart();
      this.buildIssuesChart();
      this.buildUsageChart();
    }
  }

  private computeSummary(): void {
    const totals = this.metricsData.reduce(
        (acc, cur) => {
          acc.totalDisplayed += cur.totalTimesDisplayed;
          acc.totalOpened += cur.totalOpenedFromWidget;
          acc.totalSent += cur.totalMessageSent; // AI Responses
          acc.totalBlocked += cur.totalMessageBlockedByFilter;
          acc.totalRouted += cur.totalMessageRoutedToAI;
          acc.totalResponses += cur.totalResponseReceivedFromAI; // User Messages
          if (cur.totalResponseReceivedFromAI > 0) {
            acc.responseTimeSum += cur.avgTimeTakenToRespond * cur.totalResponseReceivedFromAI;
            acc.responseCount += cur.totalResponseReceivedFromAI;
          }
          return acc;
        },
        {
          totalDisplayed: 0,
          totalOpened: 0,
          totalSent: 0,
          totalBlocked: 0,
          totalRouted: 0,
          totalResponses: 0,
          responseTimeSum: 0,
          responseCount: 0
        }
    );

    this.summary.totalDisplayed = totals.totalDisplayed;
    this.summary.totalOpened = totals.totalOpened;
    this.summary.totalSent = totals.totalSent;           // AI Responses
    this.summary.totalBlocked = totals.totalBlocked;
    this.summary.totalRouted = totals.totalRouted;
    this.summary.totalResponses = totals.totalResponses;   // User Messages
    // Convert ms to seconds, round to whole number.
    this.summary.avgResponse =
        totals.responseCount > 0
            ? Math.round(totals.responseTimeSum / totals.responseCount / 1000)
            : 0;
  }

  private buildTimelineChart(): void {
    // Group metrics by date (YYYY-MM-DD) to calculate the daily average response time.
    const grouped: { [date: string]: { sum: number; count: number } } = {};
    this.metricsData.forEach(metric => {
      const date = metric.reportDate.split('T')[0];
      if (!grouped[date]) {
        grouped[date] = { sum: 0, count: 0 };
      }
      if (metric.totalResponseReceivedFromAI > 0) {
        grouped[date].sum += metric.avgTimeTakenToRespond * metric.totalResponseReceivedFromAI;
        grouped[date].count += metric.totalResponseReceivedFromAI;
      }
    });
    const dates = Object.keys(grouped).sort();
    const seriesData = dates.map(date =>
        grouped[date].count > 0
            ? Math.round(grouped[date].sum / grouped[date].count / 1000)
            : 0
    );

    this.timelineSeries = [
      { name: 'Avg Response (s)', data: seriesData }
    ];
    this.timelineChart = {
      type: 'line',
      height: 350,
      toolbar: { show: true }
    };
    this.timelineXAxis = {
      categories: dates,
      title: { text: 'Date' }
    };
    this.timelineStroke = { curve: 'smooth', width: 3 };
    this.timelineTooltip = {
      y: { formatter: (val: number) => `${val} s` }
    };
  }

  private buildIssuesChart(): void {
    // Total messages blocked vs. routed.
    const totalBlocked = this.metricsData.reduce((sum, m) => sum + m.totalMessageBlockedByFilter, 0);
    const totalRouted = this.metricsData.reduce((sum, m) => sum + m.totalMessageRoutedToAI, 0);

    this.issuesSeries = [totalBlocked, totalRouted];
    this.issuesLabels = ['Blocked', 'Routed to AI'];
    this.issuesChart = {
      type: 'donut',
      height: 350
    };
    this.issuesTooltip = {
      y: { formatter: (val: number) => val.toString() }
    };
  }

  private buildUsageChart(): void {
    // Group by report date to aggregate usage metrics.
    const grouped: {
      [date: string]: {
        displayed: number;
        opened: number;
        sent: number;
        received: number;
      };
    } = {};
    this.metricsData.forEach(metric => {
      const date = metric.reportDate.split('T')[0];
      if (!grouped[date]) {
        grouped[date] = { displayed: 0, opened: 0, sent: 0, received: 0 };
      }
      grouped[date].displayed += metric.totalTimesDisplayed;
      grouped[date].opened += metric.totalOpenedFromWidget;
      grouped[date].sent += metric.totalMessageSent; // AI Responses
      grouped[date].received += metric.totalResponseReceivedFromAI;
    });

    const dates = Object.keys(grouped).sort();
    const displayedSeries = dates.map(date => grouped[date].displayed);
    const openedSeries = dates.map(date => grouped[date].opened);
    const sentSeries = dates.map(date => grouped[date].sent);
    const receivedSeries = dates.map(date => grouped[date].received);

    this.usageSeries = [
      { name: 'Displayed', data: displayedSeries },
      { name: 'Opened', data: openedSeries },
      { name: 'AI Responses', data: sentSeries },
      { name: 'User Messages', data: receivedSeries }
    ];
    this.usageChart = {
      type: 'bar',
      height: 350,
      stacked: true,
      toolbar: { show: true }
    };
    this.usageXAxis = {
      categories: dates,
      title: { text: 'Date' }
    };
    this.usagePlotOptions = {
      bar: { horizontal: false, columnWidth: '35%', borderRadius: 4 }
    };
    this.usageDataLabels = { enabled: false };
    this.usageLegend = { show: true };
    this.usageResponsive = [
      {
        breakpoint: 600,
        options: { plotOptions: { bar: { borderRadius: 3 } } }
      }
    ];
  }
}
